import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { AgendarStore, IState, selectCfcsDestino, selectMunicipios } from '../AgendarStore'
import { Select, Radio, RadioChangeEvent } from 'antd'
import { formatMoeda, getNumbers } from '../../../../components/common/Utils'
import { isValid as isCpfValid } from '@fnando/cpf'
import { Button, notification } from 'antd'
import { RepasseModel, TurmaModel } from '../../../../components/models/Models'
import { CfcsOrigemAutocomplete } from '../../../../components/cfcs/CfcsOrigemAutocomplete'
import { Modalidades, TabelaTurmas, TurmasPorCfc } from '../turmas/TurmasDisponiveis'
import { Show } from '../../../../components/common/Show'
import { CotistasAutocomplete } from '../../../../components/cotistas/CotistasAutoComplete'
import useAxios from 'axios-hooks'
import { TailSpin } from 'react-loader-spinner'
import { SelectComponent } from '../../../../components/common/Select'

const NovoAgendamento = () => {
  const { Layout, Title } = NovoAgendamento
  const { cotistas, isTelefoneValid, agendamento, paramsPilotar } = AgendarStore.useState()
  if (!isTelefoneValid || !agendamento.telefone) return null

  return (
    <Layout paramsPilotar={!!paramsPilotar}>
      <Title className="title-novo-agendamento">Selecione a modalidade e o município</Title>
      <div className="cotista">
        <Show condition={cotistas.length > 0}>
          <SelectCotista />
        </Show>
      </div>
      <div className="cfc-origem">
        <CfcOrigem />
      </div>
      <Municipio />
      <ShowStore when={showTipoCurso}>
        <div className="tipo-curso">
          <TipoCurso />
        </div>
      </ShowStore>
      <div className="repasses">
        <RepassesOptions />
      </div>
      <div className="tabela-turmas">
        <TabelaTurmas />
      </div>
    </Layout>
  )
}

NovoAgendamento.Title = styled.h2`
  color: #3a3a3a;
  font-weight: 600;
`
NovoAgendamento.Layout = styled.div<{ paramsPilotar?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 12px;
  grid-row-gap: 7px;
  grid-template-areas:
    'title title'
    'cfc-origem cfc-origem'
    'municipio municipio'
    'cotista cotista'
    'cfc-destino cfc-destino'
    'tipo-curso tipo-curso'
    'repasses repasses'
    'tabela-turmas tabela-turmas'
    'gerar-boleto gerar-boleto';
  .title-novo-agendamento {
    grid-area: title;
  }
  .municipio {
    grid-area: municipio;
    margin-bottom: 7px;
  }
  .cfc-origem {
    grid-area: cfc-origem;
    margin-bottom: 7px;
  }
  .cotista {
    grid-area: cotista;
    margin-bottom: 7px;
  }
  .cfc-destino {
    grid-area: cfc-destino;
  }
  .tipo-curso {
    grid-area: tipo-curso;
  }
  .tabela-turmas {
    grid-area: tabela-turmas;
  }
  .lista-turmas {
    grid-area: lista-turmas;
    margin: 50px 0px;
  }
  .check-possui-repasse-origem {
    grid-area: check-possui-repasse-origem;
  }
  .repasses {
    grid-area: repasses;
  }
  .gerar-boleto {
    grid-area: gerar-boleto;
  }
  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'title'
      'cfc-origem'
      'municipio'
      'cotista'
      'tipo-curso'
      'repasses'
      'cfc-destino'
      'tabela-turmas'
      'gerar-boleto';
  }
`
const Municipio = () => {
  const state = AgendarStore.useState()
  const {
    municipio,
    agendamento: { tipoCurso, cfcOrigem },
  } = AgendarStore.useState()

  const { Layout } = Municipio

  const dispatch = AgendarStore.useDispatch()
  const municipios = selectMunicipios(state)
  let cfcsDestino = selectCfcsDestino(state)

  useEffect(() => {
    dispatch(AgendarStore.thunks.getCfcsDestino())
  }, [])

  useEffect(() => {
    if (!!cfcsDestino && cfcOrigem) {
      cfcsDestino = selectCfcsDestino(state)
      dispatch(AgendarStore.thunks.getTurmasByMunicipio(cfcsDestino, tipoCurso))
    }
  }, [municipio])

  function handleChange(value: any) {
    dispatch(AgendarStore.actions.changeMunicipio(value))
  }

  return (
    <Layout className="municipio">
      <SelectComponent
        label="Selecione o Município"
        data={municipios}
        titleOption="Informe o município onde será realizado o curso"
        onChange={handleChange}
        value={municipio || null}
      />
    </Layout>
  )
}

Municipio.Layout = styled.div`
  .ant-select-selector {
    border-radius: 4px !important;
  }
`

const CfcOrigem = () => {
  const { municipio, cfcsOrigem, paramsPilotar } = AgendarStore.useState()
  const dispatch = AgendarStore.useDispatch()

  useEffect(() => {
    dispatch(AgendarStore.thunks.getCfcsOrigem())
  }, [])

  const handleSelect = (value: any, option: any) => {
    const cfcOrigem = cfcsOrigem.find((x) => x.cnpj === option.cfccnpj)
    dispatch(AgendarStore.thunks.changeCfcAndSearchCotistas(cfcOrigem))
  }

  useEffect(() => {
    handleSelect(null, { cfccnpj: paramsPilotar?.cnpjCfcPilotar })
  }, [paramsPilotar, cfcsOrigem])

  const selectedCfc = useMemo(() => {
    return cfcsOrigem.find((x) => x.cnpj === paramsPilotar?.cnpjCfcPilotar) ?? undefined
  }, [paramsPilotar, cfcsOrigem])

  return (
    <CfcsOrigemAutocomplete
      label="CFC de origem:"
      onChange={() => dispatch(AgendarStore.actions.changeCfcOrigem(null))}
      onSelect={handleSelect}
      showDescription
      selectedCfcValue={selectedCfc}
      value={selectedCfc?.cnpj}
      disabled={!!selectedCfc}
      underAutoComplete={!!paramsPilotar}
    />
  )
}

const CfcDestino = () => {
  const state = AgendarStore.useState()
  const { agendamento, municipio } = state
  const cfcsDestino = selectCfcsDestino(state)
  const dispatch = AgendarStore.useDispatch()
  const [cleaningTimeout, setCleaningTimeout] = useState<any>()

  useEffect(() => {
    dispatch(AgendarStore.thunks.getCfcsDestino())
  }, [])

  function handleChange(value: any) {
    const cfcDestino = cfcsDestino.find((x) => x?.fantasia === value)
    dispatch(AgendarStore.actions.changeCfcDestino(cfcDestino || null))
  }

  return (
    <div className="cfc-destino">
      <label>
        CFC de Destino:
        <br />
        <Select
          value={agendamento?.cfcDestino?.fantasia || ''}
          onChange={handleChange}
          size="large"
          style={{ width: '100%' }}
          data-cy="cfcDestino"
          showSearch
          disabled={!municipio}
        >
          <Select.Option value={''}>
            <em>Informe o CFC de destino</em>
          </Select.Option>
          {cfcsDestino.map((c) => {
            return (
              <Select.Option key={c.id} value={c.fantasia}>
                <a href="#lista-turmas" style={{ color: 'black' }}>
                  {c.fantasia}
                </a>
              </Select.Option>
            )
          })}
        </Select>
      </label>
    </div>
  )
}

const TextTipoCurso = styled.span`
  color: #3a3a3a;
`
const showTipoCurso = (state: IState) => {
  const { cfcOrigem } = state.agendamento
  const { paramsPilotar } = state
  return !!cfcOrigem && !paramsPilotar
}
const TipoCurso = () => {
  const { Layout } = TipoCurso

  const dispatch = AgendarStore.useDispatch()
  const {
    status,
    paramsPilotar,
    agendamento: { cpf, cfcOrigem, tipoCurso },
  } = AgendarStore.useState()

  const cpfCompleto = cpf.replace(/[_.-]/g, '').length === 11

  const state = AgendarStore.useState()
  let cfcsDestino = selectCfcsDestino(state)

  useEffect(() => {
    if (cfcOrigem) {
      cfcsDestino = selectCfcsDestino(state)
      dispatch(AgendarStore.thunks.getTurmasByMunicipio(cfcsDestino, tipoCurso))
    }
  }, [tipoCurso])

  const handleChange = (e: any) => {
    dispatch(AgendarStore.actions.changeTipoCurso(e.target.value))
  }

  if (!cfcOrigem) return null

  return (
    <Layout className="tipo">
      <Radio.Group
        onChange={handleChange}
        value={tipoCurso}
        disabled={!cpfCompleto || status !== 'NOVO'}
      >
        <Radio style={{ borderColor: 'black' }} key={'PRIMEIRA VIA'} value={'PRIMEIRA_VIA'}>
          <TextTipoCurso>PRIMEIRA VIA</TextTipoCurso>
        </Radio>
        <Radio style={{ borderColor: 'black' }} key={'RENOVAÇÃO'} value={'RENOVACAO'}>
          <TextTipoCurso>RENOVAÇÃO</TextTipoCurso>
        </Radio>
      </Radio.Group>
    </Layout>
  )
}
TipoCurso.Layout = styled.div`
  .ant-radio-group.ant-radio-group-outline {
    display: flex;
  }
  .ant-radio-inner::after {
    background-color: #ebbc00;
  }
  .ant-radio-checked .ant-radio-inner {
    border: 1px solid #ebbc00;
  }
`

const RepassesOptions = () => {
  const { Layout } = RepassesOptions
  const dispatch = AgendarStore.useDispatch()
  const {
    status,
    hasRepasses,
    agendamento: { cpf, cfcOrigem },
  } = AgendarStore.useState()

  const cpfCompleto = cpf.replace(/[_.-]/g, '').length === 11

  const handleChange = (e: any) => {
    dispatch(AgendarStore.actions.changeOptionRepasse(e.target.value))
  }

  if (!cfcOrigem) return null
  return (
    <Layout>
      <Radio.Group
        onChange={handleChange}
        value={hasRepasses}
        disabled={!cpfCompleto || status !== 'NOVO'}
      >
        <Radio style={{ borderColor: 'black' }} key="SEM REPASSES" value={false}>
          <TextTipoCurso>SEM REPASSES</TextTipoCurso>
        </Radio>
        <Radio style={{ borderColor: 'black' }} key="COM REPASSES" value={true}>
          <TextTipoCurso>COM REPASSES</TextTipoCurso>
        </Radio>
      </Radio.Group>
    </Layout>
  )
}
RepassesOptions.Layout = styled.div`
  .ant-radio-group.ant-radio-group-outline {
    display: flex;
  }
  .ant-radio-inner::after {
    background-color: #fbba3e;
  }
  .ant-radio-checked .ant-radio-inner {
    border: 1px solid #fbba3e;
  }
`

const SelectCotista = () => {
  const { cotistas, paramsPilotar } = AgendarStore.useState()
  const dispatch = AgendarStore.useDispatch()

  const handleSelect = (value: any, option: any) => {
    const cotista = cotistas?.find((x: any) => x.id === option.cotistaId) || null
    dispatch(AgendarStore.actions.changeCotista(cotista))
  }

  if (!!paramsPilotar) return null

  return (
    <CotistasAutocomplete
      label="Instrutor:"
      cotistas={cotistas}
      onChange={() => dispatch(AgendarStore.actions.changeCotista(null))}
      onSelect={handleSelect}
      showDescription
    />
  )
}

const ListaTurmas = () => {
  const {
    turmas,
    agendamento: { cfcDestino, tipoCurso },
  } = AgendarStore.useState()
  const dispatch = AgendarStore.useDispatch()

  if (!turmas) return null

  useEffect(() => {
    dispatch(AgendarStore.thunks.getTurmas(cfcDestino?.id, tipoCurso))
  }, [cfcDestino, tipoCurso])

  return (
    <div className="lista-turmas">
      <a id="lista-turmas"></a>
      <Modalidades />
      <TurmasPorCfc />
    </div>
  )
}

const Repasses = () => {
  const { Container, ListaRepasses, RepasseOrigem, ValorTotal } = Repasses
  const {
    agendamento: { cpf, cfcOrigem, cfcDestino, tipoCurso },
  } = AgendarStore.useState()

  const [{ data: repasses, loading, error }, refetch] = useAxios(
    {
      url: '/repasses/agendamento-regular',
      params: { cpf, cfcOrigemCnpj: cfcOrigem?.cnpj, cfcDestinoCnpj: cfcDestino?.cnpj, tipoCurso },
    },
    { useCache: false, manual: true }
  )

  useEffect(() => {
    if (!cfcDestino) return
    refetch().catch((e) => {})
  }, [cpf, cfcOrigem, cfcDestino, tipoCurso])

  if (error) {
    const message = error.response?.data?.message || 'Erro ao buscar repasses!'
    notification.error({ message })
  }

  if (!repasses || !cfcDestino) return null

  return (
    <div className="repasses" style={{ marginLeft: 'auto' }}>
      <TailSpin
        color="#FBBA3E"
        height={50}
        width={50}
        visible={loading}
        wrapperStyle={{
          width: '50px',
          margin: '5px auto',
        }}
      />
      <Show condition={!loading}>
        <Container>
          <ListaRepasses repasse={repasses} />
          <RepasseOrigem repasse={repasses} />
          <ValorTotal valorTotal={repasses?.valorTotal} />
        </Container>
      </Show>
    </div>
  )
}
Repasses.Container = styled.div`
  width: 250px;
  justify-self: end;
`
Repasses.ListaRepasses = ({ repasse }: { repasse: RepasseModel }) => {
  let repassesExibicao = JSON.parse(repasse?.repassesExibicao || '[]')

  return repassesExibicao.map((r: any, i: any) => <Repasse repasse={r} key={i} />)
}
Repasses.RepasseOrigem = ({ repasse }: { repasse: RepasseModel }) => {
  let repassesExibicao = JSON.parse(repasse?.repassesExibicao || '[]')

  if (repassesExibicao.length <= 0) return null

  const {
    agendamento: { cotista, cfcOrigem },
  } = AgendarStore.useState()

  const totalRepassesExibicao = repassesExibicao.reduce((v1: any, v2: any) => v1 + v2.valor, 0)
  const valorCfcOrigem = (repasse?.valorTotal || 0) - totalRepassesExibicao

  if (cotista && valorCfcOrigem > 0)
    return <Repasse repasse={{ descricao: `${cotista.nome}`, valor: valorCfcOrigem }} />
  if (valorCfcOrigem > 0)
    return <Repasse repasse={{ descricao: cfcOrigem?.fantasia, valor: valorCfcOrigem }} />

  return null
}
Repasses.ValorTotal = ({ valorTotal }: { valorTotal: number }) => {
  return (
    <Repasse
      repasse={{
        descricao: <strong>Valor Total</strong>,
        valor: <strong>{formatMoeda(valorTotal)}</strong>,
      }}
    />
  )
}

const Repasse = ({ repasse }: { repasse: { descricao: any; valor: any } }) => {
  const { Container, Separador } = Repasse
  return (
    <Container>
      <div> {repasse.descricao} </div>
      <Separador />
      <div> {formatMoeda(repasse?.valor)} </div>
    </Container>
  )
}
Repasse.Container = styled.div`
  display: flex;
`
Repasse.Separador = styled.div`
  flex: 1;
  border-bottom: 1px dashed #ccc;
  top: -5px;
  position: relative;
  margin: 0 8px;
`

function removeAcentos(str: string) {
  return str?.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

const GerarBoleto = () => {
  const { Layout, validate, createBoleto } = GerarBoleto
  const [loading, setLoading] = useState(false)

  const state = AgendarStore.useState()
  const dispatch = AgendarStore.useDispatch()

  async function handleClick() {
    const { valid, message } = await validate(state)
    if (!state.agendamento.telefone)
      return notification.error({ message: 'Informe o número de telefone' })
    if (state.agendamento.telefone && state.agendamento.telefone.length < 15) {
      return notification.error({ message: 'Um telefone válido deve conter 11 dígitos' })
    }

    if (!valid) return notification.error({ message })
    setLoading(true)
    createBoleto(dispatch).finally(() => setLoading(false))
  }

  return (
    <Layout className="gerar-boleto">
      <Button
        data-cy="btn-gerar-boleto"
        onClick={handleClick}
        loading={loading}
        size="large"
        type="primary"
      >
        Gerar cobrança
      </Button>
    </Layout>
  )
}
GerarBoleto.Layout = styled.div`
  max-width: 1100px;
  margin-left: auto;
`
GerarBoleto.validate = async (state: IState) => {
  const { cpf, nome, renach, cfcOrigem, cfcDestino } = state.agendamento

  const renachFormatado = renach?.replace(/[_.-]/g, '')

  const invalid = (message: string) => ({ valid: false, message })

  if (!cpf) return invalid('Informe o CPF do aluno!')
  if (!isCpfValid(cpf)) return invalid('CPF Inválido!')
  if (!nome) return invalid('Informe o Nome do aluno!')
  if (!renach) return invalid('Informe o RENACH do aluno!')
  if (renachFormatado && renachFormatado.length < 11) return invalid('RENACH possui 9 dígitos!')
  if (!cfcOrigem?.id) return invalid('Informe o CFC de origem!')
  if (!cfcDestino?.cnpj) return invalid('Informe o CFC de destino!')
  if (
    removeAcentos(cfcOrigem.municipio) === 'GOIANIA' &&
    removeAcentos(cfcOrigem.municipio) !== removeAcentos(cfcDestino.municipio)
  ) {
    return invalid('Se o CFC de Origem é de Goiânia, o CFC de Destino também deve ser de Goiânia')
  }

  return { valid: true, message: '' }
}
GerarBoleto.isEmailValid = (email: string) => {
  const re = /^[^\s@]+@[^\s@]+$/
  return re.test(email)
}
GerarBoleto.isMunicipiosValid = (municipioOrigem: string, municipioDestino: string) => {
  const removeAccents = (value: any) => value?.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  if (removeAccents(municipioDestino) === 'GOIANIA') return true
  return removeAccents(municipioDestino) === removeAccents(municipioOrigem)
}
GerarBoleto.createBoleto = (dispatch: any) => {
  return dispatch(AgendarStore.thunks.createAgendamento())
}

const ShowStore = ({ children, when }: { children: any; when: (state: IState) => boolean }) => {
  const state = AgendarStore.useState()
  return when(state) ? children : null
}

export default NovoAgendamento
